import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet as WalletIcon, Power, Sparkles, Compass, Heart } from "lucide-react";

import { useAuth } from "../AuthContext";
import { t } from "../i18n";
import { formatPhone } from "../utils/phone";
import LanguageSelector from "../components/LanguageSelector";
import InstallAppCard from "../components/InstallAppCard";
import TimeMasterCard from "../components/TimeMasterCard";
import ModuleCard from "../components/ModuleCard";
import LifeIntroPopup from "../components/LifeIntroPopup";
import DrawingIcon from "../components/icons/DrawingIcon";
import LanguageIcon from "../components/icons/LanguageIcon";
import MathIcon from "../components/icons/MathIcon";
import MeditationIcon from "../components/icons/MeditationIcon";

const MODULES = [
  {
    slug: "mind",
    to: "/mind-master",
    icon: MeditationIcon,
    titleKey: "mind_master_title",
    descKey: "mind_master_desc",
    badgeKey: "open_now",
    active: true,
  },
  {
    slug: "drawing",
    to: "/drawing",
    icon: DrawingIcon,
    titleKey: "module_drawing_title",
    descKey: "module_drawing_desc",
    badgeKey: "open_now",
    active: true,
  },
  {
    slug: "language",
    to: "/language",
    icon: LanguageIcon,
    titleKey: "module_language_title",
    descKey: "module_language_desc",
    badgeKey: "open_now",
    active: true,
  },
  {
    slug: "math",
    to: "/math",
    icon: MathIcon,
    titleKey: "module_math_title",
    descKey: "module_math_desc",
    badgeKey: "open_now",
    active: true,
  },
];

export default function Landing() {
  const { user, lang, config, logout } = useAuth();
  const navigate = useNavigate();
  const balance = user?.wallet_balance ?? 0;
  const currency = config?.currency_symbol || "₹";

  return (
    <div className="page-shell" data-testid="landing-page">
      <LifeIntroPopup />
      <div className="flex items-center justify-between gap-3">
        <button
          data-testid="wallet-pill"
          onClick={() => navigate("/wallet")}
          className="flex items-center gap-2 bg-emerald-400 hover:bg-emerald-500 text-white px-4 py-2 rounded-full font-bold shadow-[0_4px_0_rgba(0,0,0,0.1)] border-2 border-emerald-600/20 active:translate-y-1 active:shadow-none transition-all"
        >
          <WalletIcon size={18} strokeWidth={2.5} />
          {currency}<span data-testid="wallet-balance">{balance}</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            data-testid="help-a-friend-btn"
            onClick={() => navigate("/help-a-friend")}
            aria-label={t(lang, "help_a_friend")}
            title={t(lang, "help_a_friend")}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 text-white border-2 border-white shadow-[0_4px_0_rgba(0,0,0,0.15)] btn-squish"
          >
            <motion.span
              style={{ display: "inline-flex" }}
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            >
              <Heart size={18} strokeWidth={2.75} fill="currentColor" />
            </motion.span>
          </button>
          <button
            data-testid="daily-reflection-btn"
            onClick={() => navigate("/daily-reflection")}
            aria-label={t(lang, "daily_reflection")}
            title={t(lang, "daily_reflection")}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white border-2 border-white shadow-[0_4px_0_rgba(0,0,0,0.15)] btn-squish"
          >
            <motion.span
              style={{ display: "inline-flex" }}
              animate={{ rotate: 360 }}
              transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
            >
              <Compass size={18} strokeWidth={2.75} />
            </motion.span>
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full ring-2 ring-white animate-pulse" />
          </button>
          <LanguageSelector compact />
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="p-2 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
            aria-label="logout"
          >
            <Power size={18} strokeWidth={2.5} className="text-slate-600" />
          </button>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1 rounded-full border-2 border-slate-900/10 text-sm font-bold text-slate-600">
          <Sparkles size={16} strokeWidth={2.5} className="text-pink-500" /> {t(lang, "welcome")}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">
          {t(lang, "platform_name")}
        </h1>
        <p className="text-base sm:text-lg text-cyan-50/95 mt-1 font-semibold drop-shadow-[0_2px_8px_rgba(15,23,42,0.45)]">{t(lang, "platform_tagline")}</p>
      </motion.div>

      <div className="mt-6 flex flex-col gap-3">
        {MODULES.map((m, i) => (
          <ModuleCard
            key={m.slug}
            slug={m.slug}
            to={m.to}
            icon={m.icon}
            titleKey={m.titleKey}
            descKey={m.descKey}
            badgeKey={m.badgeKey}
            active={m.active}
            lang={lang}
            onNavigate={navigate}
            index={i}
          />
        ))}
      </div>

      <TimeMasterCard />

      <InstallAppCard lang={lang} />

      <p className="mt-6 text-center text-xs text-cyan-100/80 font-semibold drop-shadow-[0_2px_6px_rgba(15,23,42,0.4)]">
        {formatPhone(user?.phone)}
      </p>
      <div className="mt-2 mb-1 text-center">
        <button
          data-testid="privacy-policy-link"
          onClick={() => navigate("/privacy-policy")}
          className="text-xs text-slate-500 hover:text-slate-700 font-semibold underline underline-offset-4 decoration-slate-400/60 active:translate-y-0.5 transition"
        >
          {t(lang, "privacy_policy")}
        </button>
      </div>
    </div>
  );
}
